import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useParams,
} from "react-router-dom";
import DefaultLayout from "@shared-layouts/DefaultLayout";
import MainLayout from "@shared-layouts/MainLayout";
import ProjectsPage from "@pages/ProjectsPage";
import NotFoundPage from "@pages/NotFoundPage";

import DataLabellingPage from "@pages/Operations/DataLabellingPage";
import DataLabellingSelectionPage from "@pages/Operations/DataLabellingPage/DataLabellingSelectionPage";
import DiverseSelectionPage from "@pages/Operations/DiverseSelectionPage";
import AugmentationPage from "@pages/Operations/AugmentationPage";
import AutoLabellingPage from "@pages/Operations/AutoLabellingPage";
import TrainingPage from "@pages/Operations/TrainingPage";
import EvaluationPage from "@pages/Operations//EvaluationPage";
import ActiveSelectionPage from "@pages/Operations/ActiveLearningPage";
import TargetSelectionPage from "@pages/Operations/TargetLearningPage";
import EvaluationLogsPage from "@pages/Operations/EvaluationPage/EvaluationLogsPage";
import ProtectedRoute from "./ProtectedRoute";
import ActiveSelectionHistoryPage from "@pages/Operations/ActiveLearningPage/ActiveLearningHistoryPage";
import TargetSelectionHistoryPage from "@pages/Operations/TargetLearningPage/TargetLearningHistoryPage";
import LogsPage from "@pages/Tasks/LogsPage";
import UploadDataPage from "@pages/Tasks/UploadDataPage";
import LabelsPage from "@pages/Tasks/LabelsPage";
import ClarifierHistoryPage from "@pages/Tasks/ClarifierHistoryPage";
import InferencePage from "@pages/Operations/InferencePage";
import InferenceLogsPage from "@pages/Operations/InferencePage/InferenceLogsPage";
import ExportPage from "@pages/Operations/ExportPage";
import ClarifierPage from "@pages/Operations/CalrifierPage";
import ClarifierLogsPage from "@pages/Operations/CalrifierPage/ClarifierLogsPage";
import RelevantDataSelectionPage from "@pages/Operations/RelevantDataSelectionPage";
import TieredHardnessPage from "@pages/Operations/TieredHardnessPage";
import ModelBasedALPage from "@pages/Operations/ModelBasedALPage";
import SliceMarkingPage from "@pages/Operations/SliceMarkingPage";
import SliceMarkingDetailPage from "@pages/Operations/SliceMarkingPage/SliceMarkingDetailPage";
import ALCorrectionPage from "@pages/Operations/ALCorrectionPage";
import ALCorrectionDetailPage from "@pages/Operations/ALCorrectionPage/ALCorrectionDetailPage";
import MedSAM2PropagationPage from "@pages/Operations/MedSAM2PropagationPage";
import MedSAM2PropagationListPage from "@pages/Operations/MedSAM2PropagationPage/MedSAM2PropagationListPage";
import MedSAM2PropagationDetailPage from "@pages/Operations/MedSAM2PropagationPage/MedSAM2PropagationDetailPage";
import ValidationLogsPage from "@pages/Operations/ValidationLogsPage";
import ExportCTInstancesPage from "@pages/Operations/ExportCTInstancesPage";
import Ct2dModelTrainingPage from "@pages/Operations/Ct2dModelTrainingPage";
import Ct3dModelTrainingPage from "@pages/Operations/Ct3dModelTrainingPage";
import Ct3dGroundTruthEvaluationPage from "@pages/Operations/Ct3dGroundTruthEvaluationPage";
import NewLogin from "@pages/Auth/NewLogin";
import NiftiFileReader from "@pages/Operations/DataLabellingPage/NiftiFileReader";
import NiftiFileUploadPage from "@pages/Operations/DataLabellingPage/NiftiFileUploadPage";
import CloudPointPage from "@pages/CloudPointPage/CloudPointPage";
import MmWaveVisualization from "@pages/CloudPointPage/MmWaveVisualization";
import LabelLayerTest from "../labels/LabelLayerTest";
import NifiFiles from "@pages/dummy/NifiFiles";
import DummySegmentationDataLabellingPage from "@pages/dummy/DummySegmentationDataLabellingPage";

// Component to handle default redirect for operations page
const OperationsRedirect = () => {
  const { project_type, projectId } = useParams();
  
  // For 3d-ct_segmentation_-_(clarifier), redirect to tiered-hardness
  if (project_type === '3d-ct_segmentation_-_(clarifier)') {
    return <Navigate to={`/${project_type}/operations/${projectId}/tiered-hardness`} replace />;
  }
  
  // Default redirect to data-labelling for other project types
  return <Navigate to={`/${project_type}/operations/${projectId}/data-labelling`} replace />;
};

const router = createBrowserRouter(
  [
    {
      path: "/auth/login",
      element: <NewLogin/>,
    },
    {
      path: "/nifti-file-reader",
      element: <NiftiFileReader/>,
    },
    {
      path: "/nifti-file-upload",
      element: <NiftiFileUploadPage/>,
    },
    {
      path: "/dummy/nifti-files",
      element: <NifiFiles/>,
    },
    {
      path: "/dummy/segmentation-labelling-page",
      element: <DummySegmentationDataLabellingPage/>,
    },
    {
      path: "/cloud-point",
      element: <CloudPointPage/>,
    },
    {
      path: "/cloud-point2",
      element: <MmWaveVisualization/>,
    },
    {
      path: "/label-layer-test",
      element: <LabelLayerTest/>,
    },
    {
      path: "/projects",
      element: <MainLayout />,
      children: [
        {
          path: "",
          element: <ProtectedRoute path="" element={<ProjectsPage />}/>,
        },
      ],
    },
    {
      path: "/:project_type/operations/:projectId",
      element: <DefaultLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute path="" element={<OperationsRedirect />} />,
        },
        {
          path: "data-labelling",
          element: <ProtectedRoute path="" element={<DataLabellingPage />} />,
        },
        {
          path: "data-labelling/selection",
          // Here is the main labelling data
          element: (
            <ProtectedRoute path="" element={<DataLabellingSelectionPage />} />
          ),
        },
        {
          path: "diverse-selection",
          element: (
            <ProtectedRoute path="" element={<DiverseSelectionPage />} />
          ),
        },
        {
          path: "initial-selection",
          element: (
            <ProtectedRoute path="" element={<DiverseSelectionPage />} />
          ),
        },
        {
          path: "tiered-hardness",
          element: (
            <ProtectedRoute path="" element={<TieredHardnessPage />} />
          ),
        },
        {
          path: "tiered-hardness/start",
          element: (
            <ProtectedRoute path="" element={<TieredHardnessPage />} />
          ),
        },
        {
          path: "slice-marking",
          element: <ProtectedRoute path="" element={<SliceMarkingPage />} />,
        },
        {
          path: "slice-marking/:fileName",
          element: <ProtectedRoute path="" element={<SliceMarkingDetailPage />} />,
        },
        {
          path: "auto-labelling-correction",
          element: <ProtectedRoute path="" element={<ALCorrectionPage />} />,
        },
        {
          path: "auto-labelling-correction/:fileName",
          element: <ProtectedRoute path="" element={<ALCorrectionDetailPage />} />,
        },
        {
          path: "medsam-2-propagation",
          element: <ProtectedRoute path="" element={<MedSAM2PropagationPage />} />,
        },
        {
          path: "propagated-slices-correction",
          element: <ProtectedRoute path="" element={<MedSAM2PropagationListPage />} />,
        },
        {
          path: "propagated-slices-correction/:fileName",
          element: <ProtectedRoute path="" element={<MedSAM2PropagationDetailPage />} />,
        },
        {
          path: "validation-logs",
          element: <ProtectedRoute path="" element={<ValidationLogsPage />} />,
        },
        {
          path: "export-ct-instances",
          element: <ProtectedRoute path="" element={<ExportCTInstancesPage />} />,
        },
        {
          path: "model-al",
          element: (
            <ProtectedRoute path="" element={<ModelBasedALPage />} />
          ),
        },
        {
          path: "2d-model/new-training",
          element: <ProtectedRoute path="" element={<Ct2dModelTrainingPage />} />,
        },
        {
          path: "3d-model/new-training",
          element: <ProtectedRoute path="" element={<Ct3dModelTrainingPage />} />,
        },
        {
          path: "3d-model/ground-truth-evaluation",
          element: <ProtectedRoute path="" element={<Ct3dGroundTruthEvaluationPage />} />,
        },
        {
          path: "augmentation",
          element: <ProtectedRoute path="" element={<AugmentationPage />} />,
        },
        {
          path: "auto-labelling",
          element: <ProtectedRoute path="" element={<AutoLabellingPage />} />,
        },
        {
          path: "training",
          element: <ProtectedRoute path="" element={<TrainingPage />} />,
        },
        {
          path: "evaluation",
          element: <ProtectedRoute path="" element={<EvaluationPage />} />,
        },
        {
          path: "evaluation/logs",
          element: <ProtectedRoute path="" element={<EvaluationLogsPage />} />,
        },
        {
          path: "evaluation/logs/:logId",
          element: <ProtectedRoute path="" element={<EvaluationLogsPage />} />,
        },
        {
          path: "clarifier",
          element: <ProtectedRoute path="" element={<ClarifierPage />} />,
        },
        {
          path: "clarifier/history",
          element: <ProtectedRoute path="" element={<ClarifierLogsPage />} />,
        },
        {
          path: "active-selection",
          element: <ProtectedRoute path="" element={<ActiveSelectionPage />} />,
        },
        {
          path: "active-selection/history",
          element: (
            <ProtectedRoute path="" element={<ActiveSelectionHistoryPage />} />
          ),
        },
        {
          path: "target-selection",
          element: <ProtectedRoute path="" element={<TargetSelectionPage />} />,
        },
        {
          path: "target-selection/history",
          element: (
            <ProtectedRoute path="" element={<TargetSelectionHistoryPage />} />
          ),
        },
        {
          path: "inference",
          element: <ProtectedRoute path="" element={<InferencePage />} />,
        },
        {
          path: "inference/logs",
          element: <ProtectedRoute path="" element={<InferenceLogsPage />} />,
        },
        {
          path: "relevant-data-selection",
          element: <ProtectedRoute path="" element={<RelevantDataSelectionPage />} />,
        },
        {
          path: "export",
          element: <ProtectedRoute path="" element={<ExportPage />} />,
        },
      ],
    },
    {
      path: "/:project_type/options/:projectId",
      element: <DefaultLayout />,
      children: [
        {
          path: "logs",
          element: <ProtectedRoute path="" element={<LogsPage />} />,
        },
        {
          path: "upload-data",
          element: <ProtectedRoute path="" element={<UploadDataPage />} />,
        },
        {
          path: "labels",
          element: <ProtectedRoute path="" element={<LabelsPage />} />,
        },
        {
          path: "clarifier-history",
          element: <ProtectedRoute path="" element={<ClarifierHistoryPage />} />,
        },
      ],
    },
    {
      path: "/",
      element: <Navigate to="/auth/login" />,
    },

    { path: "*", element: <NotFoundPage /> },
  ],
  { basename: "/" }
);

function AppRoutes() {
  return <RouterProvider router={router} />;
}

export default AppRoutes;
